/**
 * Captura de eventos de producto en el navegador.
 *
 * Módulo de cliente: depende de `instrumentation-client` (PostHog). Los nombres
 * de los eventos viven en `lib/product-events.ts`, módulo puro que pueden
 * importar los server components; este fichero los reexporta para no romper a
 * los ~40 consumidores de cliente que ya los importaban de aquí.
 */
import { captureEvent } from '@/instrumentation-client'
import { PRODUCT_EVENTS } from './product-events'
import type { DeliveryState } from '@/server/domain/santa-video/delivery'
import type { VideoLocale } from '@/server/domain/santa-video/locale'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

export { PRODUCT_EVENTS } from './product-events'

export type MemoryVideoAnalyticsProperties = {
  character_id: string
  call_id?: string
  provider: string
  include_child: boolean
  watermark: boolean
  mime_type?: string
  duration_seconds?: number
  reason_code?: string
}

export type CheckoutAnalyticsProperties = {
  package_id: string
  package_name: string
  amount: number
  currency: string
  minutes: number
  assigned_variant?: 'package_selector' | 'post_trial_single_offer'
  checkout_variant?: 'package_selector' | 'post_trial_single_offer' | 'revenuecat'
  transaction_id?: string
  provider?: string
  failure_stage?: 'authentication' | 'checkout_creation' | 'checkout_response' | 'redirect'
  reason_code?: string
}

/**
 * La página de entrega del vídeo. Sólo el id INTERNO del pedido: ni el token
 * (es la credencial de la página) ni el nombre del niño (ADR-054). Descargar y
 * compartir llevan sólo el id; la vista, además, el estado y el pedido.
 */
export type VideoDeliveryActionProperties = {
  video_order_id: string
}

export type VideoDeliveryViewedProperties = VideoDeliveryActionProperties & {
  delivery_state: DeliveryState
  story: VideoStoryId
  video_locale: VideoLocale
}

/** Cómo salió el enlace de referido del dispositivo (DoD 8, KPI share activation). */
export type ReferralShareAnalyticsProperties = {
  method: 'web_share' | 'clipboard'
}

/**
 * Interacciones del tracker de Papá Noel. Deliberadamente NO se emite nada por
 * el tic del reloj ni por el avance del trineo: son cientos de eventos por
 * visita que no responden a ninguna pregunta.
 */
export type SantaTrackerAnalyticsProperties = {
  /** Fase de la experiencia en el momento de la interacción. */
  phase: 'preparing' | 'liftoff' | 'traveling' | 'completed'
  locale: string
  /** Ciudad abierta (`santa_tracker_city_opened`). */
  stop_id?: string
  /** Cómo salió el enlace (`santa_tracker_shared`). */
  method?: 'web_share' | 'clipboard'
  /** Qué CTA se pulsó (`santa_tracker_cta_clicked`). */
  cta?: 'video_call' | 'characters' | 'free_santa'
}

/** Los CTA medidos del tracker: el contrato de analítica es su único dueño. */
export type SantaTrackerCta = NonNullable<SantaTrackerAnalyticsProperties['cta']>

export type LocaleSuggestionAnalyticsProperties = {
  /** Locale de la página en la que se aterrizó. */
  from_locale: string
  /** Locale ofrecido. */
  to_locale: string
  /** Primer idioma declarado por el navegador; para ver a quién estamos leyendo mal. */
  browser_language: string
}

export type CallAnalyticsProperties = {
  call_id?: string
  character_id: string
  call_type: string
  provider: string
  failure_stage?: string
  reason_code?: string
}

export function captureProductEvent(
  event: (typeof PRODUCT_EVENTS)[keyof typeof PRODUCT_EVENTS],
  properties:
    | CheckoutAnalyticsProperties
    | CallAnalyticsProperties
    | MemoryVideoAnalyticsProperties
    | ReferralShareAnalyticsProperties
    | VideoDeliveryActionProperties
    | VideoDeliveryViewedProperties
    | SantaTrackerAnalyticsProperties
    | LocaleSuggestionAnalyticsProperties
    | Record<string, unknown>
): void {
  captureEvent(event, properties)
}
